// ==========================================
// Core Types for Video Editor Application
// ==========================================

export type OverlayType = 'rectangle' | 'rounded-rectangle' | 'circle' | 'ellipse' | 'polygon' | 'text' | 'image' | 'blur';

export type BlurType = 'blur' | 'pixelate';

// Geometry types
export interface Point {
  x: number;
  y: number;
}

export interface Size {
  width: number;
  height: number;
}

export interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Color with alpha channel
export interface RGBA {
  r: number;
  g: number;
  b: number;
  a: number;
}

// Style properties for overlays
export interface OverlayStyle {
  fillColor: RGBA;
  borderColor: RGBA;
  borderWidth: number;
  opacity: number;
}

// Text-specific properties
export interface TextProperties {
  content: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  fontStyle: 'normal' | 'italic';
  textAlign: 'left' | 'center' | 'right';
  backgroundColor?: RGBA;
  hasBackground: boolean;
}

// Blur-specific properties
export interface BlurProperties {
  blurType: BlurType;
  intensity: number; // 1-100
}

// Image-specific properties
export interface ImageProperties {
  src: string; // Data URL or file path
  originalWidth: number;
  originalHeight: number;
  preserveAspectRatio: boolean;
}

// Polygon-specific properties
export interface PolygonProperties {
  points: Point[];
  sides: number; // For regular polygons
}

// Base overlay element
export interface OverlayElementBase {
  id: string;
  type: OverlayType;
  bounds: Bounds;
  rotation: number; // degrees 0-360
  style: OverlayStyle;
  startTime: number; // seconds
  endTime: number; // seconds
  zIndex: number;
  locked: boolean;
  visible: boolean;
  name: string;
  animation?: AnimationProperties; // Optional animation settings
}

// Shape overlay (rectangle, rounded-rectangle, circle, ellipse)
export interface ShapeOverlay extends OverlayElementBase {
  type: 'rectangle' | 'rounded-rectangle' | 'circle' | 'ellipse';
  cornerRadius?: number; // For rounded-rectangle
}

// Polygon overlay
export interface PolygonOverlay extends OverlayElementBase {
  type: 'polygon';
  polygon: PolygonProperties;
}

// Tiling properties for repeated patterns
export interface TileProperties {
  enabled: boolean;
  gap: number; // Space between tiles
  angle: number; // Angle of the pattern (usually 45)
  opacity?: number; // Optional override for tile opacity
}

// Animation types
export type AnimationType = 'none' | 'fade' | 'slide-left' | 'slide-right' | 'slide-up' | 'slide-down' | 'scale' | 'bounce';

// Animation properties for overlays
export interface AnimationProperties {
  // Entry animation
  entryType: AnimationType;
  entryDuration: number; // seconds
  entryDelay: number; // seconds after startTime
  
  // Exit animation
  exitType: AnimationType;
  exitDuration: number; // seconds
  
  // Continuous animations (loop during visible time)
  pulseEnabled: boolean;
  pulseSpeed: number; // 0.5 = slow, 2 = fast
}

// Text overlay
export interface TextOverlay extends OverlayElementBase {
  type: 'text';
  text: TextProperties;
  tile?: TileProperties;
}

// Image overlay
export interface ImageOverlay extends OverlayElementBase {
  type: 'image';
  image: ImageProperties;
  tile?: TileProperties;
}

// Blur/Pixelate overlay
export interface BlurOverlay extends OverlayElementBase {
  type: 'blur';
  blur: BlurProperties;
}

// Union type for all overlays
export type OverlayElement = ShapeOverlay | PolygonOverlay | TextOverlay | ImageOverlay | BlurOverlay;

// Video metadata
export interface VideoMetadata {
  path: string;
  name: string;
  duration: number; // seconds
  width: number;
  height: number;
  fps: number;
  codec: string;
  fileSize: number; // bytes
}

// Video Trim Range
export interface TrimRange {
  startTime: number; // seconds - in point
  endTime: number;   // seconds - out point
  enabled: boolean;  // whether trimming is active
}

export const createDefaultTrimRange = (duration: number): TrimRange => ({
  startTime: 0,
  endTime: duration,
  enabled: false,
});

// Export settings
export interface ExportSettings {
  resolution: '720p' | '1080p' | '4k' | 'original';
  quality: 'low' | 'medium' | 'high';
  format: 'mp4' | 'gif';
  preserveAudio: boolean;
  // GIF-specific
  gifFps?: number;          // frames per second for GIF (default 12)
  gifLoop?: number;         // 0 = infinite loop, else loop count
  // Branding
  addBranding?: boolean;    // Append "Made with Overlay Studio" watermark
  // Draft / quick-preview mode: 720p, CRF 35, ultrafast preset — instant preview
  isDraft?: boolean;
}

// Project file structure
export interface Project {
  id: string;
  name: string;
  version: string;
  schemaVersion: number;
  createdAt: string;
  updatedAt: string;
  video: VideoMetadata | null;
  videoBlob: Blob | null;  // Store original video blob for export with audio
  trimRange: TrimRange | null;  // Video trim in/out points
  overlays: OverlayElement[];
  exportSettings: ExportSettings;
}

// History state for undo/redo
export interface HistoryState {
  overlays: OverlayElement[];
  timestamp: number;
  description: string;
}

// Tool types
export type Tool = 
  | 'select'
  | 'rectangle'
  | 'rounded-rectangle'
  | 'circle'
  | 'ellipse'
  | 'polygon'
  | 'text'
  | 'image'
  | 'blur';

// Player state
export interface PlayerState {
  isPlaying: boolean;
  currentTime: number;
  volume: number;
  isMuted: boolean;
  playbackRate: number;
}

// Timeline state
export interface TimelineState {
  zoom: number; // 1-10
  scrollPosition: number;
  snapEnabled: boolean;
  trackHeight: number;
}

// Render progress
export interface RenderProgress {
  isRendering: boolean;
  progress: number; // 0-100
  estimatedTimeRemaining: number; // seconds
  currentPhase: 'preparing' | 'rendering' | 'encoding' | 'finalizing';
  error?: string;
}

// Selection state
export interface SelectionState {
  selectedIds: string[];
  hoveredId: string | null;
}

// Drag state for canvas interactions
export interface DragState {
  isDragging: boolean;
  dragType: 'move' | 'resize' | 'rotate' | 'create' | null;
  startPoint: Point | null;
  currentPoint: Point | null;
  resizeHandle: ResizeHandle | null;
}

export type ResizeHandle = 
  | 'nw' | 'n' | 'ne'
  | 'w' | 'e'
  | 'sw' | 's' | 'se'
  | 'rotate';

// API Response types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface VideoImportResponse {
  metadata: VideoMetadata;
  thumbnailPath: string;
}

export interface RenderRequest {
  project: Project;
  outputPath: string;
}

export interface RenderResponse {
  jobId: string;
  outputPath: string;
}

// Keyboard shortcuts
export interface KeyboardShortcut {
  key: string;
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
  action: string;
}

// Default values factory functions
export const createDefaultTileProperties = (): TileProperties => ({
  enabled: false,
  gap: 100,
  angle: 0,
});

export const createDefaultAnimationProperties = (): AnimationProperties => ({
  entryType: 'none',
  entryDuration: 0.5,
  entryDelay: 0,
  exitType: 'none',
  exitDuration: 0.5,
  pulseEnabled: false,
  pulseSpeed: 1,
});

export const createDefaultStyle = (): OverlayStyle => ({
  fillColor: { r: 99, g: 102, b: 241, a: 0.5 },
  borderColor: { r: 99, g: 102, b: 241, a: 1 },
  borderWidth: 2,
  opacity: 100,
});

export const createDefaultTextProperties = (): TextProperties => ({
  content: 'Text',
  fontFamily: 'Arial',
  fontSize: 32,
  fontWeight: 'normal',
  fontStyle: 'normal',
  textAlign: 'center',
  hasBackground: false,
  backgroundColor: { r: 0, g: 0, b: 0, a: 0.5 },
});

export const createDefaultBlurProperties = (): BlurProperties => ({
  blurType: 'blur',
  intensity: 50,
});

export const createDefaultExportSettings = (): ExportSettings => ({
  resolution: '1080p',
  quality: 'high',
  format: 'mp4',
  preserveAudio: true,
  gifFps: 12,
  gifLoop: 0,
  addBranding: false,
  isDraft: false,
});

export const createDefaultProject = (): Project => ({
  id: '',
  name: 'Untitled Project',
  version: '1.0.0',
  schemaVersion: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  video: null,
  videoBlob: null,
  trimRange: null,
  overlays: [],
  exportSettings: createDefaultExportSettings(),
});

// Utility functions
export const rgbaToString = (color: RGBA): string => {
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
};

export const rgbaToHex = (color: RGBA): string => {
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(color.r)}${toHex(color.g)}${toHex(color.b)}`;
};

export const hexToRgba = (hex: string, alpha = 1): RGBA => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { r: 0, g: 0, b: 0, a: alpha };
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
    a: alpha,
  };
};

export const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const ms = Math.floor((seconds % 1) * 100);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
};

export const parseTime = (timeString: string): number => {
  const parts = timeString.split(':');
  if (parts.length !== 2) return 0;
  const [mins, secMs] = parts;
  const [secs, ms = '0'] = secMs.split('.');
  return parseInt(mins) * 60 + parseInt(secs) + parseInt(ms) / 100;
};

